import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { fetchCoachBadges } from "./coachServices";

// Coach Mode shell state: the attention badge on the header mail icon and
// the team selection shared by Home, Team, My Circle and CacheShare™ (a
// coach can manage several teams; the choice must not reset when switching
// tabs).

const CoachModeContext = createContext(null);

const NO_BADGES = { attention: 0, pendingApprovals: 0 };
const BADGE_POLL_MS = 60000;

// The same team can be stored twice on a coach (stale per-season ids,
// primary + admin-assigned duplicates): dedupe by name + league per view.
const dedupeTeams = (list) => {
  const seen = new Set();
  return (list || []).filter((t) => {
    const key = `${(t.currentTeam || "").trim().toLowerCase()}|${t.league || ""}|${t.leagueAdmin ? 1 : 0}`;
    if (!t.teamId || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

// `pollBadges` is off while the coach awaits verification: the attention
// queue is built from roster deals, which a pending coach cannot see.
export const CoachModeProvider = ({ children, pollBadges = true }) => {
  const [badges, setBadges] = useState(NO_BADGES);
  const [selection, setSelection] = useState({ teams: [], leagueAdmin: false, view: "coach", team: null, season: "", seasons: [] });

  // The attention and approvals counts are for the team on screen.
  const badgeTeam = selection.team;
  const badgeSeason = selection.season;
  const refreshBadges = useCallback(async () => {
    try {
      const data = await fetchCoachBadges(badgeTeam, badgeSeason);
      if (data) setBadges({ attention: data.attention || 0, pendingApprovals: data.pendingApprovals || 0 });
    } catch (err) {
      // decorative
    }
  }, [badgeTeam, badgeSeason]);

  useEffect(() => {
    if (!pollBadges) {
      setBadges(NO_BADGES);
      return undefined;
    }
    refreshBadges();
    const timer = setInterval(refreshBadges, BADGE_POLL_MS);
    return () => clearInterval(timer);
  }, [pollBadges, refreshBadges]);

  const absorbSelector = useCallback((selector, chosenTeam) => {
    if (!selector) return;
    setSelection((prev) => {
      const teams = Array.isArray(selector.teams) && selector.teams.length ? dedupeTeams(selector.teams) : prev.teams;
      const leagueAdmin = prev.leagueAdmin || !!selector.leagueAdmin;
      const team = chosenTeam || prev.team || teams.find((t) => !t.leagueAdmin) || teams[0] || null;
      return {
        ...prev,
        teams,
        leagueAdmin,
        team,
        seasons: Array.isArray(selector.seasons) ? selector.seasons : prev.seasons,
        season: selector.season || "",
      };
    });
  }, []);

  const selectTeam = useCallback((team) => setSelection((prev) => ({ ...prev, team, season: "" })), []);
  const selectSeason = useCallback((season) => setSelection((prev) => ({ ...prev, season })), []);
  const switchView = useCallback((view) => setSelection((prev) => {
    const list = prev.teams.filter((t) => (view === "league" ? t.leagueAdmin : !t.leagueAdmin));
    return { ...prev, view, team: list[0] || prev.team, season: "" };
  }), []);

  const value = useMemo(
    () => ({ badges, refreshBadges, selection, absorbSelector, selectTeam, selectSeason, switchView }),
    [badges, refreshBadges, selection, absorbSelector, selectTeam, selectSeason, switchView],
  );
  return <CoachModeContext.Provider value={value}>{children}</CoachModeContext.Provider>;
};

// For pages that also render outside Coach Mode (the admin CacheShare tool
// at /cacheshare-tool): the shell state, or null outside the provider.
export const useCoachModeOptional = () => useContext(CoachModeContext);

export const useCoachMode = () => {
  const ctx = useCoachModeOptional();
  if (!ctx) throw new Error("useCoachMode must be used inside CoachModeProvider");
  return ctx;
};
